import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { HangForm } from '@components/forms/hang/hang-form.component';
import {
  HangFormOverlayType,
  HangFormView,
  HangSheetHeight,
} from '@components/forms/hang/hang-form.types';
import { PuebloRouter } from '@routing/pueblo-router.service';
import { urlSignal } from '@utils/signal-utils';
import { Hang } from 'pueblo-types';
import { PageLayout } from 'types/ui-types';
import { HangFormOverlayStore } from '../hang-form-overlay.store';

const VALID_URL_PATTERNS = [
  /hang\/.*?\/edit(\?.*)?$/,
  /hang\/create(\?.*)?$/,
  /tabs\/hangs(\?.*)?$/,
];

@Component({
  selector: 'hang-sheet',
  templateUrl: 'hang-sheet.component.html',
  styleUrl: 'hang-sheet.component.scss',
  imports: [
    HangForm,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    NgClass,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HangSheet {
  protected readonly sheetRef = inject(MatBottomSheetRef);
  protected readonly puebloRouter = inject(PuebloRouter);
  protected readonly store = inject(HangFormOverlayStore);

  protected readonly HangSheetHeight = HangSheetHeight;
  protected readonly HangFormOverlayType = HangFormOverlayType;
  protected readonly PageLayout = PageLayout;

  readonly $hang = input<Hang | undefined>(undefined, { alias: 'hang' });
  readonly $timespan = input<Hang | undefined>(undefined, {
    alias: 'timespan',
  });

  readonly created = output<Hang>();
  readonly updated = output<Hang>();
  readonly cancel = output<void>();
  readonly pendingHang = output<Hang | undefined>();
  readonly viewChange = output<HangFormView>();
  readonly changesMade = output<boolean>();

  protected readonly $hangForm = viewChild<HangForm>('hangForm');
  protected readonly $sheetContent =
    viewChild.required<ElementRef<HTMLElement>>('sheetContent');

  protected readonly $formView = signal<HangFormView>(HangFormView.MAIN);
  protected readonly $formPendingHang = signal<Hang | undefined>(undefined);

  protected readonly $url = urlSignal();

  protected readonly $visible = computed(
    () =>
      this.store.visible() &&
      this.store.overlayType() === HangFormOverlayType.SHEET,
  );

  constructor() {
    effect(() => {
      const formView = this.$formView();
      untracked(() => {
        switch (formView) {
          case HangFormView.MAIN:
            this.store.setSheetHeight(HangSheetHeight.HALF);
            break;
          case HangFormView.DETAILS:
            this.store.setSheetHeight(HangSheetHeight.HALF);
            break;
          case HangFormView.NEW_PUEBLO:
            this.store.setSheetHeight(HangSheetHeight.HALF);
            break;
          case HangFormView.TIME_SELECT:
            this.store.setSheetHeight(HangSheetHeight.HALF);
            break;
        }
      });
    });

    effect(() => {
      const formPendingHang = this.$formPendingHang();
      const visible = this.store.visible();
      untracked(() => {
        if (formPendingHang && visible) {
          this.store.setPendingHang(formPendingHang);
        }
      });
    });

    effect(() => {
      const url = this.$url();
      untracked(() => {
        if (
          this.store.visible() &&
          !VALID_URL_PATTERNS.some((pattern) => pattern.test(url))
        ) {
          this.store.close(false);
        }
      });
    });
  }

  protected handleCreated(hang: Hang) {
    this.created.emit(hang);
    this.store.close(/*goBack=*/ false);
    this.puebloRouter.goToHang(hang.resourceName, { replaceUrl: true });
  }

  protected handleUpdated(hang: Hang) {
    this.updated.emit(hang);
    this.store.close(/*goBack=*/ false);
    this.puebloRouter.goToHang(hang.resourceName, { replaceUrl: true });
  }

  protected handleCancel() {
    this.cancel.emit();
    this.store.close();
  }

  protected toggleHeight() {
    if (this.store.sheetHeight() === HangSheetHeight.COLLAPSED) {
      this.store.setSheetHeight(HangSheetHeight.HALF);
    } else {
      this.$hangForm()?.setView(HangFormView.MAIN);
      setTimeout(() => {
        this.store.setSheetHeight(HangSheetHeight.COLLAPSED);
      }, 0);
    }
  }
}
